import { BarList, type BarListData, useChart } from '@chakra-ui/charts';
import { Box, HStack, Heading, SimpleGrid, Skeleton, Stat } from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';

import { type Member, type MemberProgression, type MembersStat, StatsService } from '@/client';

const StatsCards = ({ statsData, isLoading }: { statsData: MembersStat | undefined; isLoading: boolean }) => {
  if (!statsData) {
    return null;
  }

  return (
    <SimpleGrid columns={{ base: 1, sm: 2 }} gap={4} px={4}>
      {/* Card for Actions Count */}
      <Stat.Root borderWidth="1px" p="4" rounded="md" bg="primary.500">
        <Stat.Label fontSize="lg" color={'white'}>
          Nombre d'actions avec pilotes
        </Stat.Label>
        <Skeleton width="full" loading={isLoading}>
          <HStack>
            <Stat.ValueText textWrap={'nowrap'} fontSize="3xl" color={'white'}>
              {statsData.actions_with_reponsible_count} / {statsData.actions_count}
            </Stat.ValueText>
            <Stat.HelpText color={'white'}>
              {Math.floor(
                ((statsData.actions_with_reponsible_count as number) / (statsData.actions_count as number)) * 100
              )}{' '}
              % des actions ont des pilotes
            </Stat.HelpText>
          </HStack>
        </Skeleton>
      </Stat.Root>

      {/* Card for Indicators Count */}
      <Stat.Root borderWidth="1px" p="4" rounded="md" bg="primary.500">
        <Stat.Label fontSize="lg" color={'white'}>
          Nombre d'indicateurs avec pilotes
        </Stat.Label>
        <Skeleton width="full" loading={isLoading}>
          <HStack>
            <Stat.ValueText textWrap={'nowrap'} fontSize="3xl" color={'white'}>
              {statsData.indicators_with_reponsible_count} / {statsData.indicators_count}
            </Stat.ValueText>
            <Stat.HelpText color={'white'}>
              {Math.floor(
                ((statsData.indicators_with_reponsible_count as number) / (statsData.indicators_count as number)) * 100
              )}{' '}
              % des indicateurs ont des pilotes
            </Stat.HelpText>
          </HStack>
        </Skeleton>
      </Stat.Root>
    </SimpleGrid>
  );
};

function BarListMembersDistribution({
  users,
  data,
  title,
}: {
  users: Member[];
  data: MemberProgression[];
  title: string;
}) {
  const getUser = (id: number) => users.find((user) => user.id === id);
  const chart = useChart<BarListData>({
    sort: { by: 'value', direction: 'desc' },
    data: data
      .sort((a, b) => (a.count ?? 0) - (b.count ?? 0))
      .map((item) => ({
        name: `${getUser(item.user_id)?.first_name} ${getUser(item.user_id)?.last_name}`,
        value: item.count ?? 0,
      })),
    series: [{ name: 'name', color: 'teal.subtle' }],
  });

  return (
    <>
      <Heading size={'lg'} mb={4}>
        {title}
      </Heading>

      <BarList.Root chart={chart}>
        <BarList.Content>
          <BarList.Bar />
          <BarList.Value />
        </BarList.Content>
      </BarList.Root>
    </>
  );
}

function getMembersStatsQueryOptions({ slug }: { slug: string }) {
  return {
    queryKey: ['stats', { slug }],
    queryFn: async () => {
      return await StatsService.readMembersStats({
        companySlug: slug,
      });
    },
  };
}

export const DashboardMembersView = () => {
  const { slug } = useParams({ strict: false });

  const { data: statsData, isLoading } = useQuery({
    ...getMembersStatsQueryOptions({
      slug,
    }),
  });

  return (
    <>
      <StatsCards statsData={statsData} isLoading={isLoading} />
      <SimpleGrid columns={{ base: 1, lg: 2 }} gap="4" p="4">
        <Box bg="white" borderWidth="1px" p="4" rounded="md">
          <BarListMembersDistribution
            users={statsData?.users || []}
            data={statsData?.per_action_progression || []}
            title="Distributions des actions par pilotes"
          />
        </Box>
        <Box bg="white" borderWidth="1px" p="4" rounded="md">
          <BarListMembersDistribution
            users={statsData?.users || []}
            data={statsData?.per_indicator_progression || []}
            title="Distributions des indicateurs par pilotes"
          />
        </Box>
      </SimpleGrid>
    </>
  );
};

export default DashboardMembersView;
